import { addMonths, format, parseISO } from 'date-fns'
import Decimal from 'decimal.js'

import { CriadorParcelasBase } from '@/domain/simulador/criador-parcelas-base'
import type { TSimulacaoParcela } from '@/features/simulador/brasil/types/simulacao.type'
import { roundFloat } from '@/utils/number.utils'

type TCalcularValoresComPercentualResult = {
  percentualEntrada: number
  valorEntrada: number
  valorCadaEntrada: number[]
}

export type TFormaDePagamento = '1' | '2' | '3' | '4' | ''

export class GerenciadorEntradasPrice {
  private static readonly PERCENTUAL_BASE = 100

  criarGrupoInicial(
    datasSinais: string[],
    percentualSinal = 0,
    ato?: { data: string; percentualAto: number },
    valorBase?: number,
  ) {
    const { percentualEntrada, valorEntrada, valorCadaEntrada } = this.calcularValoresComPercentual(
      percentualSinal,
      ato?.percentualAto,
      datasSinais.length,
      ato?.data,
      valorBase,
    )

    const datasEntradas = [...(ato?.data ? [ato.data] : []), ...datasSinais]
    const grupos = this.criarParcelas(datasEntradas, valorCadaEntrada)

    return {
      formaDePagamento: (grupos.length > 0 ? grupos.length.toString() : '') as TFormaDePagamento,
      percentual: percentualEntrada,
      valorTotal: valorEntrada,
      grupos,
    }
  }

  criarNovoGrupo(
    formaDePagamento: TFormaDePagamento,
    percentual: number,
    valorProposto: number,
    dataSimulacao: string,
  ) {
    const quantidade = this.obterQuantidade(formaDePagamento)
    const valorTotal = this.calcularValorTotal(valorProposto, percentual)
    const valorCadaEntrada = this.calcularValorCadaEntrada(valorTotal, quantidade)

    const datas = this.gerarDatasSequenciais(dataSimulacao, quantidade)
    return this.criarParcelas(datas, valorCadaEntrada)
  }

  calcularAjustePorPercentual(
    formaDePagamento: TFormaDePagamento,
    percentual: number,
    valorProposto: number,
    datasGruposExistentes: string[],
    dataReferencia: string,
  ) {
    const quantidade = this.obterQuantidade(formaDePagamento)
    const valorTotal = this.calcularValorTotal(valorProposto, percentual)
    const valorCadaEntrada = this.calcularValorCadaEntrada(valorTotal, quantidade)

    const grupos = this.datasExistem(datasGruposExistentes)
      ? this.atualizarValoresGruposExistentes(datasGruposExistentes, valorCadaEntrada)
      : this.criarNovoGrupo(formaDePagamento, percentual, valorProposto, dataReferencia)

    return {
      valorTotal,
      grupos,
    }
  }

  calcularAjustePorValor(
    formaDePagamento: TFormaDePagamento,
    valorEntrada: number,
    valorProposto: number,
    datasGruposExistentes: string[],
    dataReferencia: string,
  ) {
    const quantidade = this.obterQuantidade(formaDePagamento)
    const percentual = this.calcularPercentual(valorEntrada, valorProposto)
    const valorCadaEntrada = this.calcularValorCadaEntrada(valorEntrada, quantidade)

    const grupos = this.datasExistem(datasGruposExistentes)
      ? this.atualizarValoresGruposExistentes(datasGruposExistentes, valorCadaEntrada)
      : this.criarNovoGrupo(formaDePagamento, percentual, valorProposto, dataReferencia)

    return {
      percentual,
      grupos,
    }
  }

  private datasExistem(datas: string[]): boolean {
    return datas.length > 0
  }

  private atualizarValoresGruposExistentes(datas: string[], valorCadaEntrada: number[]): TSimulacaoParcela[] {
    return datas.map((data, index) => CriadorParcelasBase.criar(data, valorCadaEntrada[index]))
  }

  calcularAjustePorValorProposto(
    valorProposto: number,
    entradas?: {
      formaDePagamento?: TFormaDePagamento | null
      percentual?: number | null
      grupos: Array<{ data: string | null; valor: number }>
    } | null,
  ) {
    if (!entradas || !entradas.formaDePagamento || !entradas.percentual) return
    const quantidade = this.obterQuantidade(entradas.formaDePagamento)
    const valorTotal = this.calcularValorTotal(valorProposto, entradas.percentual)
    const valorCadaEntrada = this.calcularValorCadaEntrada(valorTotal, quantidade)

    const gruposValidos = entradas.grupos
      .map((grupo, index) => (grupo.data ? CriadorParcelasBase.criar(grupo.data, valorCadaEntrada[index]) : null))
      .filter((grupo) => grupo !== null)

    return {
      formaDePagamento: entradas.formaDePagamento,
      percentual: entradas.percentual,
      valorTotal,
      grupos: entradas.formaDePagamento ? gruposValidos : [],
    }
  }

  ajustarQuantidadeGrupos(
    formaDePagamento: TFormaDePagamento,
    percentual: number,
    gruposExistentes: Array<{ data: string | null; valor: number }>,
    dataSimulacao: string,
    valorTotal: number,
  ): { grupo: TSimulacaoParcela[]; valorTotal: number; percentual: number } {
    const novaQuantidade = this.obterQuantidade(formaDePagamento)
    const datas = this.calcularDatasParaQuantidade(gruposExistentes, novaQuantidade, dataSimulacao)
    const valorCadaEntrada = this.calcularValorCadaEntrada(valorTotal, novaQuantidade)
    return {
      grupo: this.atualizarValoresGruposExistentes(datas, valorCadaEntrada),
      valorTotal,
      percentual,
    }
  }

  private calcularDatasParaQuantidade(
    gruposExistentes: Array<{ data: string | null; valor: number }>,
    novaQuantidade: number,
    dataSimulacao: string,
  ): string[] {
    const datasExistentes = gruposExistentes.map((g) => g.data).filter((d): d is string => Boolean(d))
    if (novaQuantidade <= datasExistentes.length) return datasExistentes.slice(0, novaQuantidade)

    const dataReferencia = datasExistentes.at(-1) ?? dataSimulacao
    const mesInicio = datasExistentes.length > 0 ? 1 : 0
    return [
      ...datasExistentes,
      ...Array.from({ length: novaQuantidade - datasExistentes.length }, (_, i) =>
        format(addMonths(parseISO(dataReferencia), i + mesInicio), 'yyyy-MM-dd'),
      ),
    ]
  }

  calcularAjustePorParcela(
    valoresEntradas: number[],
    valorProposto: number,
  ): { novoValorTotal: number; novoPercentual: number } {
    const novoValorTotal = roundFloat(
      valoresEntradas.reduce((acc, valor) => acc.plus(valor), new Decimal(0)).toNumber(),
      2,
    )

    const novoPercentual = this.calcularPercentual(novoValorTotal, valorProposto)

    return { novoValorTotal, novoPercentual }
  }

  private calcularValoresComPercentual(
    percentualSinal: number,
    percentualAto: number | undefined,
    quantidadeSinais: number,
    dataAto: string | undefined,
    valorBase?: number,
  ): TCalcularValoresComPercentualResult {
    const percentualEntrada = percentualAto ? percentualSinal + percentualAto : percentualSinal
    const valorEntrada = valorBase
      ? new Decimal(valorBase).mul(percentualEntrada).div(GerenciadorEntradasPrice.PERCENTUAL_BASE).toNumber()
      : 0
    const quantidadeDeEntradas = quantidadeSinais + (dataAto ? 1 : 0)
    const valorCadaEntrada: number[] = this.calcularValorCadaEntrada(valorEntrada, quantidadeDeEntradas)

    return { percentualEntrada, valorEntrada, valorCadaEntrada }
  }

  private criarParcelas(datas: string[], valores: number[]): TSimulacaoParcela[] {
    if (!valores.length || !datas?.length) return []
    return datas.map((data, index) => CriadorParcelasBase.criar(data, valores[index]))
  }

  private obterQuantidade(formaDePagamento: TFormaDePagamento): number {
    return Number.parseInt(formaDePagamento, 10) || 0
  }

  private calcularValorTotal(valorProposto: number, percentual: number): number {
    return roundFloat(
      new Decimal(valorProposto).mul(percentual).div(GerenciadorEntradasPrice.PERCENTUAL_BASE).toNumber(),
      2,
    )
  }

  private calcularPercentual(valorTotal: number, valorProposto: number): number {
    if (!valorProposto) return 0

    return roundFloat(
      new Decimal(valorTotal).mul(GerenciadorEntradasPrice.PERCENTUAL_BASE).div(valorProposto).toNumber(),
      2,
    )
  }

  private calcularValorCadaEntrada(valorTotal: number, quantidade: number): number[] {
    if (quantidade <= 0) return []
    if (valorTotal === 0) return []

    if (quantidade === 1) return [valorTotal]

    const valorParcelaBase = roundFloat(new Decimal(valorTotal).div(quantidade).toNumber(), 2)
    const parcelasEntrada = Array.from({ length: quantidade }, () => valorParcelaBase)

    const somaSemUltima = new Decimal(valorParcelaBase).mul(quantidade - 1)
    const valorParcelaEntradaAjustada = roundFloat(new Decimal(valorTotal).minus(somaSemUltima).toNumber(), 2)
    parcelasEntrada[quantidade - 1] = valorParcelaEntradaAjustada

    return parcelasEntrada
  }

  private gerarDatasSequenciais(dataInicio: string, quantidade: number): string[] {
    return Array.from({ length: quantidade }, (_, index) =>
      format(addMonths(parseISO(dataInicio), index), 'yyyy-MM-dd'),
    )
  }
}
